import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { MediaAsset, PlaylistItem } from '@ghostscreen/contracts';
import { formatNumber } from '../../i18n';
import { contentKindLabel } from '../content/buildContentLibrary';
import { isVideoAsset } from '../content/mediaPreview';
import { MediaThumb } from '../content/MediaThumb';

type PlaylistDrawerItemsProps = {
  items: PlaylistItem[];
  mediaById: ReadonlyMap<string, MediaAsset>;
  busy: boolean;
  onNudgeDuration: (item: PlaylistItem, delta: number) => void;
  onMove: (itemId: string, direction: -1 | 1) => void;
  onRemove: (itemId: string) => void;
};

export function PlaylistDrawerItems({
  items,
  mediaById,
  busy,
  onNudgeDuration,
  onMove,
  onRemove,
}: PlaylistDrawerItemsProps) {
  const { t } = useTranslation(['playlists', 'common']);
  if (items.length === 0) {
    return (
      <div
        className="rounded-xl border-[1.5px] border-dashed px-4 py-[22px] text-center text-sm"
        style={{ borderColor: 'var(--gs-line)', color: 'var(--gs-muted)' }}
      >
        {t('items.empty')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, index) => {
        const asset = mediaById.get(item.mediaAssetId);
        const video = isVideoAsset(asset);
        const name = asset?.originalFilename ?? t('items.fallbackName');
        const kind = contentKindLabel(asset?.kind === 'video' ? 'video' : 'image');
        const seconds = Math.round(item.durationSeconds);

        return (
          <div
            key={item.id}
            className="flex flex-wrap items-center gap-2 rounded-xl border px-3 py-2.5 sm:gap-3"
            style={{
              background: 'var(--gs-panel2)',
              borderColor: 'var(--gs-line)',
            }}
          >
            <span
              className="w-[18px] shrink-0 text-center text-[13px] font-bold"
              style={{ color: 'var(--gs-muted)' }}
            >
              {formatNumber(index + 1)}
            </span>

            {asset?.previewUrl ? (
              <MediaThumb
                asset={asset}
                className="h-9 w-[52px] shrink-0 rounded-[7px] border"
              />
            ) : (
              <span
                className="grid h-9 w-[52px] shrink-0 place-items-center rounded-[7px] border font-mono text-[9px] uppercase tracking-[0.1em]"
                style={{
                  background: 'var(--gs-bg)',
                  borderColor: 'var(--gs-line)',
                  color: 'var(--gs-muted)',
                }}
              >
                {kind}
              </span>
            )}

            <span className="min-w-0 flex-1 truncate text-[14.5px] font-bold" title={name}>
              {name}
            </span>

            {video ? (
              <span
                className="shrink-0 px-1.5 text-[12.5px] font-semibold"
                style={{ color: 'var(--gs-muted)' }}
                title={t('items.fullVideoTitle')}
              >
                {t('items.fullVideo', { seconds })}
              </span>
            ) : (
              <span
                className="flex shrink-0 items-center rounded-lg border"
                style={{
                  borderColor: 'var(--gs-line)',
                  background: 'var(--gs-panel)',
                }}
                title={t('items.durationTitle')}
              >
                <button
                  type="button"
                  disabled={busy || item.durationSeconds <= 5}
                  onClick={() => onNudgeDuration(item, -5)}
                  className="h-[30px] w-7 text-[15px] font-bold disabled:opacity-40"
                  style={{ color: 'var(--gs-ink2)' }}
                >
                  −
                </button>
                <span className="min-w-[38px] text-center text-[13px] font-bold">
                  {t('items.seconds', { seconds })}
                </span>
                <button
                  type="button"
                  disabled={busy || item.durationSeconds >= 300}
                  onClick={() => onNudgeDuration(item, 5)}
                  className="h-[30px] w-7 text-[15px] font-bold disabled:opacity-40"
                  style={{ color: 'var(--gs-ink2)' }}
                >
                  +
                </button>
              </span>
            )}

            <span className="flex shrink-0 gap-1">
              <IconButton
                title={t('items.moveUp')}
                disabled={busy || index === 0}
                onClick={() => onMove(item.id, -1)}
              >
                <path d="m6 15 6-6 6 6" />
              </IconButton>
              <IconButton
                title={t('items.moveDown')}
                disabled={busy || index === items.length - 1}
                onClick={() => onMove(item.id, 1)}
              >
                <path d="m6 9 6 6 6-6" />
              </IconButton>
              <IconButton
                title={t('common:actions.remove')}
                disabled={busy}
                danger
                onClick={() => onRemove(item.id)}
              >
                <path d="m6 6 12 12M18 6 6 18" />
              </IconButton>
            </span>

          </div>
        );
      })}
    </div>
  );
}

function IconButton({
  title,
  disabled,
  danger,
  onClick,
  children,
}: {
  title: string;
  disabled?: boolean;
  danger?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className="grid h-[30px] w-[30px] place-items-center rounded-lg border disabled:opacity-35"
      style={{
        borderColor: 'var(--gs-line)',
        color: 'var(--gs-muted)',
        background: 'none',
      }}
      onMouseEnter={(event) => {
        if (disabled) return;
        event.currentTarget.style.color = danger ? 'var(--gs-bad)' : 'var(--gs-accent)';
        if (danger) event.currentTarget.style.borderColor = 'var(--gs-bad)';
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.color = 'var(--gs-muted)';
        event.currentTarget.style.borderColor = 'var(--gs-line)';
      }}
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {children}
      </svg>
    </button>
  );
}
